import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Heart, LogOut, User as UserIcon, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import {
  listMyOrders,
  getWishlist,
  removeFromWishlist,
  updateMe,
} from "../lib/customerApi";
import { formatINR } from "../lib/api";
import AddressesPane from "../components/AddressesPane";

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, signOut, refresh } = useCustomerAuth();

  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingWishlist, setLoadingWishlist] = useState(true);
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login?redirect=/account");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user !== false) {
      setProfileName(user.name || "");
      setProfilePhone(user.phone || "");
    }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) return;
    listMyOrders().then(setOrders).finally(() => setLoadingOrders(false));
    getWishlist().then(setWishlist).finally(() => setLoadingWishlist(false));
  }, [user]);

  const removeWishItem = async (slug) => {
    await removeFromWishlist(slug);
    setWishlist((w) => w.filter((p) => p.slug !== slug));
    refresh();
    toast.success("Removed from wishlist");
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateMe({ name: profileName, phone: profilePhone });
      await refresh();
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  if (!user || user === false) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#4A4A4A] text-sm">Loading…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="account-page">
      <div className="mb-10">
        <p className="eyebrow">Your atelier</p>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-3">Hello, {user.name.split(" ")[0]}</h1>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-10 md:gap-16">
        <aside className="space-y-1 border-y border-[#a89e80] md:border-0 py-3 md:py-0">
          {[
            { k: "orders", label: "Orders", icon: Package },
            { k: "wishlist", label: "Wishlist", icon: Heart },
            { k: "addresses", label: "Addresses", icon: MapPin },
            { k: "profile", label: "Profile", icon: UserIcon },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`w-full flex items-center justify-between py-3 text-[12px] tracking-[0.22em] uppercase transition-colors ${
                tab === t.k ? "text-[#1A1A1A]" : "text-[#7A7A7A] hover:text-[#1A1A1A]"
              }`}
              data-testid={`account-tab-${t.k}`}
            >
              <span className="flex items-center gap-2">
                <t.icon size={14} /> {t.label}
              </span>
              {tab === t.k && <ChevronRight size={14} />}
            </button>
          ))}
          <button
            onClick={() => {
              signOut();
              toast.success("Signed out");
              navigate("/");
            }}
            className="w-full flex items-center gap-2 py-3 text-[12px] tracking-[0.22em] uppercase text-[#7A7A7A] hover:text-[#1A1A1A]"
            data-testid="account-signout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </aside>

        <section>
          {tab === "orders" && (
            <div data-testid="account-orders-pane">
              {loadingOrders ? (
                <p className="text-[#7A7A7A] text-sm">Loading your orders…</p>
              ) : orders.length === 0 ? (
                <EmptyState
                  icon={<Package size={28} />}
                  title="No orders yet"
                  subtitle="Your future heirlooms will appear here."
                  cta={<Link to="/shop" className="underline">Browse the collection</Link>}
                />
              ) : (
                <div className="space-y-4">
                  {orders.map((o) => (
                    <div
                      key={o.id}
                      className="border border-[#a89e80] p-5 md:p-6"
                      data-testid={`account-order-${o.order_number}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="eyebrow">{o.order_number}</p>
                          <p className="font-serif-display text-2xl mt-1">
                            {o.items.length} {o.items.length === 1 ? "piece" : "pieces"}
                          </p>
                          <p className="text-[11px] text-[#7A7A7A] mt-1 tracking-wider">
                            {new Date(o.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                          </p>
                        </div>
                        <div className="text-right">
                          <StatusPill status={o.status} />
                          <p className="font-serif-display text-2xl mt-2">{formatINR(o.totals.total)}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-4">
                        {o.items.slice(0, 4).map((it, i) => (
                          <img
                            key={i}
                            src={it.image}
                            alt={it.name}
                            className="w-14 h-14 object-cover bg-[#b8af92]"
                          />
                        ))}
                        {o.items.length > 4 && (
                          <span className="w-14 h-14 flex items-center justify-center bg-[#b8af92] text-xs text-[#4A4A4A]">
                            +{o.items.length - 4}
                          </span>
                        )}
                      </div>
                      {o.tracking_number && (
                        <p className="text-[11px] mt-4 tracking-wider text-[#4A4A4A]">
                          Tracking: <span className="text-[#1A1A1A]">{o.tracking_number}</span>
                          {o.carrier && <span> · {o.carrier}</span>}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "wishlist" && (
            <div data-testid="account-wishlist-pane">
              {loadingWishlist ? (
                <p className="text-[#7A7A7A] text-sm">Loading…</p>
              ) : wishlist.length === 0 ? (
                <EmptyState
                  icon={<Heart size={28} />}
                  title="Your wishlist is empty"
                  subtitle="Tap the heart on any piece you want to revisit."
                  cta={<Link to="/shop" className="underline">Start exploring</Link>}
                />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {wishlist.map((p) => (
                    <div key={p.slug} className="group" data-testid={`wishlist-item-${p.slug}`}>
                      <Link to={`/shop/${p.slug}`}>
                        <img src={p.images[0]} alt={p.name} className="w-full aspect-[3/4] object-cover" />
                      </Link>
                      <div className="mt-3 flex items-start justify-between gap-2">
                        <div>
                          <Link to={`/shop/${p.slug}`} className="font-serif-display text-xl leading-tight">
                            {p.name}
                          </Link>
                          <p className="text-xs text-[#7A7A7A] mt-1">{formatINR(p.price)}</p>
                        </div>
                        <button
                          onClick={() => removeWishItem(p.slug)}
                          className="text-[10px] tracking-[0.22em] uppercase text-[#7A7A7A] hover:text-[#1A1A1A]"
                          data-testid={`wishlist-remove-${p.slug}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "addresses" && <AddressesPane user={user} />}

          {tab === "profile" && (
            <form onSubmit={saveProfile} className="max-w-lg space-y-5" data-testid="account-profile-pane">
              <div>
                <span className="eyebrow">Email</span>
                <p className="font-serif-display text-xl mt-1">{user.email}</p>
              </div>
              <label className="block">
                <span className="eyebrow block mb-2">Full name</span>
                <input
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="w-full border border-[#a89e80] bg-transparent px-4 py-3"
                  data-testid="profile-name"
                />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2">Phone</span>
                <input
                  value={profilePhone}
                  onChange={(e) => setProfilePhone(e.target.value)}
                  className="w-full border border-[#a89e80] bg-transparent px-4 py-3"
                  data-testid="profile-phone"
                />
              </label>
              <button
                disabled={savingProfile}
                className="bg-[#1A1A1A] text-[#c3ba9e] px-8 py-3 text-[11px] tracking-[0.28em] uppercase disabled:opacity-60"
                data-testid="profile-save"
              >
                {savingProfile ? "Saving…" : "Save changes"}
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle, cta }) {
  return (
    <div className="border border-[#a89e80] p-16 text-center">
      <div className="mx-auto w-12 h-12 flex items-center justify-center text-[#4A4A4A]">{icon}</div>
      <p className="font-serif-display text-3xl mt-4">{title}</p>
      <p className="text-[#7A7A7A] mt-2 text-sm">{subtitle}</p>
      {cta && <div className="mt-5 text-[12px] tracking-[0.22em] uppercase">{cta}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-[#c79a3a]/20 text-[#c79a3a] border-[#c79a3a]/30",
    confirmed: "bg-[#1A1A1A]/10 text-[#1A1A1A] border-[#1A1A1A]/20",
    packed: "bg-[#1A1A1A]/10 text-[#1A1A1A] border-[#1A1A1A]/20",
    shipped: "bg-[#3b6e4a]/20 text-[#3b6e4a] border-[#3b6e4a]/30",
    delivered: "bg-[#3b6e4a]/30 text-[#3b6e4a] border-[#3b6e4a]/30",
    cancelled: "bg-[#c97070]/20 text-[#c97070] border-[#c97070]/30",
    refunded: "bg-[#7a7a7a]/20 text-[#7a7a7a] border-[#7a7a7a]/30",
  };
  return (
    <span
      className={`inline-block border px-3 py-1 text-[10px] tracking-[0.22em] uppercase ${map[status] || "border-[#a89e80] text-[#4A4A4A]"}`}
    >
      {status}
    </span>
  );
}
